import { createRoot } from "react-dom/client";
import "./index.css";
// i18n must be initialized before any component that calls useTranslation().
import "./i18n";
import { registerPWA } from "./pwa";

const rootEl = document.getElementById("root")!;

// Resolved at build time by Vite so the boot splash uses the same hashed,
// cache-busted asset as the rest of the app — keeping every monkey-head
// render visually identical (see GameIcons / InteractiveMonkey / Pixi).
import {
  LOADING_FOREGROUND,
  LOADING_FOREGROUND_MUTED,
  LOADING_PAGE_BG,
  LOADING_SURFACE,
} from "./lib/loadingPalette";

const IS_MOBILE_BUILD = import.meta.env.VITE_MOBILE_BUILD === "1";

// Runtime confirmation flag read by Android's MainActivity before it hides
// the system status bar. Setting this only when the build was produced via
// `npm run build:mobile` (VITE_MOBILE_BUILD=1, which also activates the
// single-vendor-chunk Vite fallback in vite.config.ts) ensures the
// immersive status-bar hide can never run against a web-style chunked
// bundle — the combination that previously caused the Android blank
// screen. See android MainActivity.confirmRuntimeMobileChunkingFallbackThenHideStatusBar.
if (IS_MOBILE_BUILD && typeof window !== "undefined") {
  (window as unknown as { __VINE_DASH_MOBILE_CHUNKING_FALLBACK?: boolean })
    .__VINE_DASH_MOBILE_CHUNKING_FALLBACK = true;
}

// Tracks the running boot-splash mini-game so we can stop its rAF loop when
// React takes over (or when we re-install the splash for an error state).
let stopBootMiniGame: (() => void) | null = null;
// Same idea for the boot-splash progress animator — a setInterval that
// eases the bar from 0 toward 90% (capped, so it never claims "done"
// before React actually mounts) and updates the visible "NN%" label.
let stopBootProgress: (() => void) | null = null;

function installBootTitleScreen(status = "Loading game…") {
  if (stopBootMiniGame) {
    stopBootMiniGame();
    stopBootMiniGame = null;
  }
  if (stopBootProgress) {
    stopBootProgress();
    stopBootProgress = null;
  }
  // Layout intentionally mirrors <SafeTitleScreen /> after React mounts:
  //   [mini-game canvas] → [progress bar] → [greyed-out "Game Is Ready" button]
  // No "Vine Dash" title or visible "Loading…" text — those caused a
  // jarring layout / copy swap when the React shell took over. The
  // status string is still announced to screen readers via aria-live.
  rootEl.innerHTML = `
    <div role="status" aria-live="polite" style="min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.75rem;padding:1.5rem;text-align:center;background:${LOADING_PAGE_BG};color:${LOADING_FOREGROUND};font-family:'Fredoka',system-ui,-apple-system,Segoe UI,Roboto,sans-serif;">
      <div style="width:20rem;max-width:85vw;display:flex;flex-direction:column;align-items:center;gap:0.5rem;">
        <div role="progressbar" aria-label="Loading progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" style="position:relative;height:1.5rem;width:100%;overflow:hidden;border-radius:9999px;background:${LOADING_SURFACE};box-shadow:0 0 0 2px ${LOADING_FOREGROUND_MUTED}, 0 2px 12px hsla(0,0%,0%,0.4);">
          <div id="boot-progress-fill" style="height:100%;width:0%;border-radius:9999px;background:${LOADING_FOREGROUND};box-shadow:0 0 8px ${LOADING_FOREGROUND_MUTED};transition:width 320ms cubic-bezier(0.22,1,0.36,1);"></div>
          <span id="boot-progress-label" aria-hidden="true" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:0.875rem;font-weight:700;font-variant-numeric:tabular-nums;font-family:'Fredoka',sans-serif;color:#ffffff;mix-blend-mode:difference;letter-spacing:0.02em;pointer-events:none;">0%</span>
        </div>
      </div>
      <button type="button" disabled aria-disabled="true" style="margin-top:0.75rem;border-radius:9999px;padding:1rem 2.5rem;font-size:1.125rem;font-weight:700;cursor:not-allowed;border:0;font-family:'Fredoka',system-ui,sans-serif;background:hsl(220,10%,30%);color:hsla(45,50%,80%,0.7);box-shadow:0 2px 8px hsla(0,0%,0%,0.2);opacity:0.7;">Loading…</button>
      <span style="position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;">${status}</span>
    </div>
  `;

  // Animate the boot-splash progress bar 0 → 90% with a matching numeric
  // counter so the user gets immediate "something is happening" feedback
  // before React's real preloader takes over. We cap at 90% so the bar
  // doesn't claim "done" prematurely — React's `<SafeTitleScreen />`
  // replaces this DOM with its real, signal-driven progress as soon as
  // the bundle is ready, and that bar continues from wherever it joins.
  try {
    const fill = document.getElementById("boot-progress-fill") as HTMLElement | null;
    const label = document.getElementById("boot-progress-label") as HTMLElement | null;
    const bar = fill?.parentElement;
    if (fill && label) {
      const start = Date.now();
      const tau = 1800; // ms — eases asymptotically toward the cap
      const cap = 90;
      const tickProgress = () => {
        const elapsed = Date.now() - start;
        const eased = 1 - Math.exp(-elapsed / tau);
        const pct = Math.min(cap, Math.round(eased * cap));
        fill.style.width = `${pct}%`;
        label.textContent = `${pct}%`;
        if (bar) bar.setAttribute("aria-valuenow", String(pct));
      };
      tickProgress();
      const intervalId = window.setInterval(tickProgress, 80);
      stopBootProgress = () => {
        window.clearInterval(intervalId);
        // Snap to 100% on handoff so the visual reads "complete" right
        // before React swaps in its own bar.
        fill.style.width = "100%";
        label.textContent = "100%";
        if (bar) bar.setAttribute("aria-valuenow", "100");
      };
    }
  } catch {
    // Progress UI is purely cosmetic — never block boot on it.
  }

  // Note: the boot splash intentionally does NOT include a mini-game
  // canvas anymore. It briefly flashed before the React `SafeTitleScreen`
  // mounted its own `LoadingRunnerGame`, which read as a duplicate. The
  // progress bar + disabled CTA are enough early feedback; the real
  // mini-game appears once React's loader takes over.
}

// Only show the boot splash (mini-game + progress bar) on the /play
// route. Other routes — landing, /privacy, /terms, etc. — render their
// own content and don't need the game preloader UI flashing in front of
// them. Capacitor mobile builds always go through /play, so they still
// get the splash.
function shouldShowBootSplash(): boolean {
  if (typeof window === "undefined") return false;
  if (IS_MOBILE_BUILD) return true;
  // Native shell: every route resolves to the game (see App.tsx
  // catch-all), so always show the splash.
  if ((window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()) {
    return true;
  }
  const path = window.location.pathname || "/";
  return path === "/play";
}

// Boot splash policy: the pre-React splash is intentionally NOT shown
// on a normal load. React's `SafeTitleScreen` is the single,
// authoritative loading UI (running-monkey mini-game + progress bar +
// CTA). Showing the simpler pre-React bar first read as a duplicate
// "grey Loading… screen → real Game Loading screen" handoff. The
// installer is still kept for the *error recovery* path below so a
// failed bundle load still gets feedback.
void shouldShowBootSplash; // kept for future use; no-op on happy path

void import("./App.tsx")
  .then(({ default: App }) => {
    if (stopBootMiniGame) {
      stopBootMiniGame();
      stopBootMiniGame = null;
    }
    if (stopBootProgress) {
      stopBootProgress();
      stopBootProgress = null;
    }
    createRoot(rootEl).render(<App />);
  })
  .catch((e) => {
    console.error("[boot] React app failed to load:", e);
    if (shouldShowBootSplash()) {
      installBootTitleScreen("Startup recovered. Reload if the game does not continue.");
    }
  });

// Defer analytics + Sentry init until the browser is idle so the landing
// page can paint first. Both modules are no-ops without configuration, but
// dynamic imports also keep their SDKs out of the critical bundle.
const scheduleObservability = () => {
  void import("./services/sentryService").then(({ initSentry }) => initSentry());
  void import("./services/analyticsService").then(({ initAnalytics }) => initAnalytics());
  // First-touch attribution capture: reads UTM params off the entry URL
  // and (on Android) the Play install referrer if available. Sticky in
  // localStorage, so safe to call on every cold load.
  void import("./services/attributionCapture").then(({ captureAndPersistAttribution }) =>
    captureAndPersistAttribution(),
  );
};
const wIdle = window as Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
};
if (typeof wIdle.requestIdleCallback === "function") {
  wIdle.requestIdleCallback(scheduleObservability, { timeout: 4000 });
} else {
  setTimeout(scheduleObservability, 2000);
}

// Defer PWA registration until the browser is idle so the Workbox/SW chain
// (virtual:pwa-register → workbox-window) doesn't compete with critical
// render resources. Native mobile builds must not register a service worker:
// in Android WebView it can keep serving stale absolute /assets/* URLs from
// an older bundled index, which looks like "missing local game assets" even
// after Capacitor synced the fresh files into android/app/src/main/assets.
const scheduleRegister = () => {
  if (IS_MOBILE_BUILD) return;
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
  };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(() => registerPWA(), { timeout: 4000 });
  } else {
    setTimeout(() => registerPWA(), 2000);
  }
};

if (document.readyState === "complete") {
  scheduleRegister();
} else {
  window.addEventListener("load", scheduleRegister, { once: true });
}
